/**
 * 탭/뷰 전환을 URL 쿼리파라미터로 동기화하는 공통 훅.
 *
 * - 탭 전환은 navigate() **push** (히스토리에 쌓임) → 브라우저/모바일 백키로
 *   "탭 단위 백"(이전 탭 복원)이 동작함.
 * - 현재 URL의 나머지 파라미터는 그대로 보존.
 * - useOverlayBackKey가 location.state의 __overlay 마커로 오버레이 백을 처리하므로,
 *   오버레이가 열려 있는 동안에는 push하지 않도록 마커가 있으면 replace로 폴백.
 */
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function useTabSearchParam(key: string, defaultValue: string, aliases?: Record<string, string>) {
  const location = useLocation();
  const navigate = useNavigate();

  const raw = new URLSearchParams(location.search).get(key) || defaultValue;
  const value = aliases?.[raw] ?? raw;

  const setValue = useCallback(
    (next: string) => {
      const p = new URLSearchParams(location.search);
      if (next === defaultValue && !p.has(key)) return; // 기본값 동일 → 조용히 무시
      p.set(key, next);
      // 오버레이(드로어/모달) 열림 상태에서는 히스토리 오염 방지를 위해 replace
      const overlayOpen = !!(location.state as { __overlay?: boolean } | null)?.__overlay;
      navigate(`${location.pathname}?${p.toString()}`, { replace: overlayOpen });
    },
    [location.pathname, location.search, location.state, key, defaultValue, navigate],
  );

  return [value, setValue] as const;
}
